/**
 * <hero-video> — lazy-loads an autoplaying background video and fades it in
 * once it can actually play, keeping the poster <img> as the LCP element.
 *
 * The <video> is rendered with `preload="none"` and NO `autoplay`, so the
 * browser downloads nothing until this component opts in — letting the poster
 * image win LCP without competing for bandwidth. Playback starts only when the
 * slide is in view and after the page has gone idle, then the video fades in
 * over the poster on its first painted frame.
 *
 * Rendition choice (speed work, Oct 2026): the <video> may carry
 * `data-src-mobile` / `data-src-desktop` (same file, different Shopify
 * renditions) and no <source>. Right before the first load() the component
 * picks one from the viewport (750px breakpoint, same as the theme CSS), so a
 * phone requests only the smaller rendition — never both. A <video> that still
 * has <source> children is loaded as-is.
 *
 * Graceful degradation: with JS disabled, reduced motion enabled, or autoplay
 * blocked, the poster image simply stays visible.
 *
 * @example
 * <hero-video class="relative w-full h-full">
 *   <img class="aromely-hero-slide__poster" ... >
 *   <video class="aromely-hero-slide__video" preload="none" muted loop playsinline
 *          data-src-desktop="…1080p.mp4" data-src-mobile="…720p.mp4"></video>
 * </hero-video>
 */
if (!customElements.get('hero-video')) {
  const DESKTOP_QUERY = '(min-width: 750px)';

  class HeroVideo extends HTMLElement {
    /** @type {HTMLVideoElement | null} */
    #video = null;
    /** @type {IntersectionObserver | null} */
    #observer = null;
    /** Whether load()/play() has been kicked off at least once. */
    #loadRequested = false;

    connectedCallback() {
      this.#video = this.querySelector('video');
      if (!this.#video) return;

      // Respect reduced-motion: keep the static poster, never fetch the video.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      // Fade in the moment the first frame is ready to display.
      this.#video.addEventListener('playing', this.#reveal, { once: true });

      // Start only when in view (skips off-screen carousel slides) and pause
      // again when scrolled/swiped away to save bandwidth and battery.
      this.#observer = new IntersectionObserver(this.#onIntersect, {
        rootMargin: '200px',
      });
      this.#observer.observe(this);
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
      this.#video?.removeEventListener('playing', this.#reveal);
    }

    /** @param {IntersectionObserverEntry[]} entries */
    #onIntersect = (entries) => {
      const entry = entries[0];
      if (!entry || !this.#video) return;

      if (entry.isIntersecting) {
        this.#play();
      } else if (this.#loadRequested) {
        this.#video.pause();
      }
    };

    /**
     * Point the <video> at the rendition for this viewport. Runs once, before
     * the first load(); a video that ships its own <source> is left untouched.
     */
    #selectSource = () => {
      const video = this.#video;
      if (!video || video.querySelector('source') || video.getAttribute('src')) return;
      const { srcMobile, srcDesktop } = video.dataset;
      const desktop = window.matchMedia(DESKTOP_QUERY).matches;
      const src = (desktop ? srcDesktop : srcMobile) || srcDesktop || srcMobile;
      if (src) video.src = src;
    };

    #play = () => {
      if (!this.#video) return;

      // Already loaded once (returning to this slide) — just resume.
      if (this.#loadRequested) {
        this.#tryPlay();
        return;
      }

      this.#loadRequested = true;

      // Defer the first fetch+play until the browser is idle so the LCP image
      // and other above-the-fold work finish first.
      const begin = () => {
        if (!this.#video) return;
        this.#selectSource();
        // preload="none" means we must load() before play() pulls any data.
        this.#video.load();
        this.#tryPlay();
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(begin, { timeout: 2000 });
      } else {
        setTimeout(begin, 200);
      }
    };

    #tryPlay = () => {
      const promise = this.#video?.play();
      // Autoplay can be rejected (rare for muted video) — keep the poster.
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {});
      }
    };

    #reveal = () => {
      this.#video?.classList.add('is-ready');
    };
  }

  customElements.define('hero-video', HeroVideo);
}
